import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { u, IMG } from "@/lib/site";

const TIMELINE = [
    "Family Legacy",
    "Timber Industry",
    "Commission Agency & Trading",
    "Ishaan Worldlink LLP",
    "Value-Added Teak Processing & Manufacturing",
];

const TIMBER_POINTS = [
    "Customer-focused approach",
    "Understanding specific requirements",
    "Quality",
    "Specifications",
    "Reliability",
];

const COMMITMENTS = ["Quality", "Transparency", "Dependable Service", "Keeping Commitments"];

export default function About() {
    return (
        <>
            <Seo
                title="About Us | ISHAAN WORLDLINK LLP"
                description="A Gandhidham timber enterprise backed by 20+ years of industry experience and a family legacy of more than five decades. Delivering timber. Upholding commitments."
            />

            {/* HERO */}
            <section className="relative flex min-h-[62svh] items-end overflow-hidden bg-ink" data-testid="about-hero">
                <img
                    src={u(IMG.mill, 2000)}
                    srcSet={`${u(IMG.mill, 900)} 900w, ${u(IMG.mill, 1600)} 1600w, ${u(IMG.mill, 2200)} 2200w`}
                    sizes="100vw"
                    alt="Industrial timber yard with stacked sawn timber"
                    className="absolute inset-0 h-full w-full object-cover"
                    fetchPriority="high"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/30" />
                <div className="relative mx-auto w-full max-w-7xl px-4 pb-16 pt-44 sm:px-6 sm:pb-20 lg:px-8">
                    <Reveal y={16}>
                        <Eyebrow dark>About Us</Eyebrow>
                        <h1 className="mt-4 font-display text-4xl font-semibold leading-[1.02] tracking-tight text-cream sm:text-5xl lg:text-6xl">
                            DELIVERING TIMBER.
                            <br />
                            <span className="text-gold">UPHOLDING COMMITMENTS.</span>
                        </h1>
                        <p className="mt-5 max-w-xl text-base leading-relaxed text-cream/75 sm:text-lg">
                            Ishaan Worldlink LLP — a Gandhidham-based timber enterprise backed by
                            20+ years of industry experience and a family legacy spanning more
                            than five decades.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* LEGACY — white */}
            <section className="bg-cream py-20 sm:py-28" data-testid="about-legacy">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <Eyebrow>Our Story</Eyebrow>
                        <h2 className="mt-4 max-w-2xl font-display text-3xl font-semibold leading-tight tracking-tight text-ink sm:text-4xl">
                            A legacy built on timber
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid gap-px border border-line bg-line lg:grid-cols-5">
                        {TIMELINE.map((step, i) => (
                            <div key={step} className="bg-cream px-6 py-8" data-testid={`timeline-step-${i + 1}`}>
                                <Reveal delay={i * 0.07}>
                                    <p className="font-display text-xs font-semibold tracking-[0.3em] text-teak">
                                        0{i + 1}
                                    </p>
                                    <p className="mt-3 font-display text-lg font-semibold leading-snug tracking-tight text-ink">
                                        {step}
                                    </p>
                                </Reveal>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* OUR TIMBER — warm wood */}
            <section className="bg-sand py-20 sm:py-28" data-testid="about-timber">
                <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
                    <Reveal className="lg:col-span-5">
                        <Eyebrow>Our Timber</Eyebrow>
                        <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight text-ink sm:text-4xl">
                            OUR TIMBER
                        </h2>
                        <p className="mt-5 text-base leading-relaxed text-mutedw">
                            We supply a diverse range of Teak, Softwood and Hardwood to customers
                            across India.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-2">
                            {["Teak", "Softwood", "Hardwood"].map((t) => (
                                <span
                                    key={t}
                                    className="border border-ink/15 px-4 py-2 text-xs font-medium uppercase tracking-[0.14em] text-ink/80"
                                >
                                    {t}
                                </span>
                            ))}
                        </div>
                        <p className="mt-8 border-l-2 border-teak pl-4 text-sm leading-relaxed text-mutedw">
                            ISHAAN WORLDLINK LLP is a member of the Kandla Timber Association.
                        </p>
                    </Reveal>
                    <Reveal delay={0.1} className="lg:col-span-7">
                        <div className="grid gap-px border border-line bg-line sm:grid-cols-2">
                            {TIMBER_POINTS.map((p, i) => (
                                <div
                                    key={p}
                                    className={`bg-sand px-7 py-7 ${i === TIMBER_POINTS.length - 1 ? "sm:col-span-2" : ""}`}
                                >
                                    <p className="font-display text-xs font-semibold tracking-[0.3em] text-teak">
                                        0{i + 1}
                                    </p>
                                    <p className="mt-2 font-display text-lg font-semibold tracking-tight text-ink">
                                        {p}
                                    </p>
                                </div>
                            ))}
                        </div>
                        <p className="mt-6 text-sm leading-relaxed text-mutedw">
                            Our work begins with understanding what each customer actually needs —
                            the species, the form and the specification — and delivering exactly
                            that, reliably.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* OUR COMMITMENT — dark timber */}
            <section className="relative overflow-hidden bg-ink py-20 sm:py-28" data-testid="about-commitment">
                <img
                    src={u(IMG.grainDark2, 1600)}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    decoding="async"
                    className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.12]"
                />
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <Eyebrow dark>Our Commitment</Eyebrow>
                        <h2 className="mt-4 max-w-3xl font-display text-3xl font-semibold leading-tight tracking-tight text-cream sm:text-4xl lg:text-5xl">
                            Delivering Timber. Upholding Commitments.
                        </h2>
                        <p className="mt-5 max-w-xl text-base leading-relaxed text-cream/70">
                            We believe lasting relationships are built on quality, transparency
                            and dependable service — and on keeping every commitment we make.
                        </p>
                    </Reveal>
                    <div className="mt-14 grid grid-cols-2 gap-px border border-linedark bg-linedark lg:grid-cols-4">
                        {COMMITMENTS.map((c, i) => (
                            <div key={c} className="bg-ink px-6 py-10 sm:px-8" data-testid={`commitment-${i + 1}`}>
                                <Reveal delay={i * 0.08}>
                                    <p className="font-display text-xs font-semibold tracking-[0.3em] text-gold">
                                        0{i + 1}
                                    </p>
                                    <p className="mt-3 font-display text-xl font-semibold tracking-tight text-cream sm:text-2xl">
                                        {c}
                                    </p>
                                </Reveal>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* OUR NEXT CHAPTER — white */}
            <section className="bg-cream py-20 sm:py-28" data-testid="about-next-chapter">
                <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
                    <Reveal className="lg:col-span-6">
                        <Eyebrow>Our Next Chapter</Eyebrow>
                        <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl">
                            You Specify. We Process.
                        </h2>
                        <p className="mt-5 text-base leading-relaxed text-mutedw">
                            We are taking the next step: moving beyond trading into value-added
                            teak processing and manufacturing — processing imported round teak
                            logs into precision-cut timber, tailored to customer-specific
                            requirements.
                        </p>
                        <p className="mt-4 text-base leading-relaxed text-mutedw">
                            Length. Width. Thickness. Quantity. All Tailored to Your Requirement.
                        </p>
                        <Link
                            to="/contact"
                            data-testid="about-enquire-btn"
                            className="group mt-9 inline-flex items-center gap-2 bg-ink px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.14em] text-cream transition-colors duration-300 hover:bg-teak"
                        >
                            Enquire Now
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                    <Reveal delay={0.1} className="lg:col-span-6">
                        <div className="group overflow-hidden">
                            <img
                                src={u(IMG.teakLogs, 1200)}
                                alt="Imported teak round logs awaiting precision processing"
                                loading="lazy"
                                decoding="async"
                                className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
